import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Branding from './Branding';
import FeatureGate from './FeatureGate';

/**
 * Bounty Marketplace - Browse and start bounties
 */
const BountyMarketplace = ({ userTier, hasFeatureAccess, user }) => {
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBounty, setSelectedBounty] = useState(null);

  useEffect(() => {
    const fetchBounties = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/bounties/available?limit=10');
        setBounties(response.data.bounties || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching bounties:', err);
        setError('Unable to load bounties');
        setLoading(false);
      }
    };

    fetchBounties();
  }, []);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return Branding.colors.forest;
      case 'intermediate': return Branding.colors.vibrantOrange;
      case 'advanced': return Branding.colors.crimson;
      default: return Branding.colors.slateGray;
    }
  };

  const getDifficultyEmoji = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return '🟢';
      case 'intermediate': return '🟡';
      case 'advanced': return '🔴';
      default: return '⚪';
    }
  };

  const handleStartBounty = (bounty) => {
    setSelectedBounty(bounty);
  };

  if (loading) {
    return (
      <div style={{
        padding: Branding.spacing.xl,
        textAlign: 'center',
        color: Branding.colors.slateGray
      }}>
        Loading bounties...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: Branding.spacing.xl,
        textAlign: 'center',
        color: Branding.colors.crimson
      }}>
        {error}
      </div>
    );
  }

  // Bounty details view
  if (selectedBounty) {
    return (
      <div style={{
        padding: Branding.spacing.xl,
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Back button */}
        <button
          onClick={() => setSelectedBounty(null)}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: Branding.colors.tealBlue,
            fontSize: Branding.typography.fontSize.sm,
            marginBottom: Branding.spacing.lg,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: Branding.spacing.xs
          }}
        >
          ← Back to All Bounties
        </button>

        {/* Bounty header */}
        <div style={{
          backgroundColor: Branding.colors.pureWhite,
          padding: Branding.spacing.xl,
          borderRadius: Branding.borders.radius.lg,
          border: `1px solid ${Branding.colors.veryLightGray}`,
          marginBottom: Branding.spacing.lg
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: Branding.spacing.md
          }}>
            <div>
              <h1 style={{
                fontSize: Branding.typography.fontSize.xl,
                fontWeight: Branding.typography.fontWeight.bold,
                color: Branding.colors.darkSlate,
                marginBottom: Branding.spacing.xs
              }}>
                {getDifficultyEmoji(selectedBounty.difficulty)} {selectedBounty.title}
              </h1>
              <div style={{
                fontSize: Branding.typography.fontSize.sm,
                color: Branding.colors.slateGray
              }}>
                {selectedBounty.difficulty} • {selectedBounty.topics?.join(', ')}
              </div>
            </div>
            
            <div style={{
              backgroundColor: getDifficultyColor(selectedBounty.difficulty),
              color: Branding.colors.pureWhite,
              padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
              borderRadius: Branding.borders.radius.md,
              fontSize: Branding.typography.fontSize.lg,
              fontWeight: Branding.typography.fontWeight.bold
            }}>
              {selectedBounty.reward_tokens} tokens
            </div>
          </div>

          {/* 🐠 COLLABORATION MULTIPLIER UI - Show how students earn more together */}
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '2px solid #0ea5e9',
            borderRadius: Branding.borders.radius.lg,
            padding: Branding.spacing.lg,
            marginBottom: Branding.spacing.lg
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: Branding.spacing.sm
            }}>
              <span style={{ fontSize: '20px', marginRight: Branding.spacing.xs }}>🐠</span>
              <h3 style={{
                fontSize: Branding.typography.fontSize.md,
                fontWeight: Branding.typography.fontWeight.bold,
                color: '#0c4a6e',
                margin: 0
              }}>
                fish learn better in schools - work together for bonus tokens!
              </h3>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: Branding.spacing.sm,
              marginBottom: Branding.spacing.sm
            }}>
              <div style={{
                textAlign: 'center',
                padding: Branding.spacing.sm,
                backgroundColor: Branding.colors.pureWhite,
                borderRadius: Branding.borders.radius.md,
                border: '1px solid #e0e7ff'
              }}>
                <div style={{ fontSize: '16px', marginBottom: '4px' }}>🐠</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>solo</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{selectedBounty.reward_tokens} tokens</div>
              </div>
              
              <div style={{
                textAlign: 'center',
                padding: Branding.spacing.sm,
                backgroundColor: Branding.colors.pureWhite,
                borderRadius: Branding.borders.radius.md,
                border: '2px solid #10b981'
              }}>
                <div style={{ fontSize: '16px', marginBottom: '4px' }}>🐠🐠</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#059669' }}>duo</div>
                <div style={{ fontSize: '12px', color: '#059669' }}>{Math.round(selectedBounty.reward_tokens * 1.5)} tokens each</div>
              </div>
              
              <div style={{
                textAlign: 'center',
                padding: Branding.spacing.sm,
                backgroundColor: Branding.colors.pureWhite,
                borderRadius: Branding.borders.radius.md,
                border: '2px solid #f59e0b'
              }}>
                <div style={{ fontSize: '16px', marginBottom: '4px' }}>🐠🐠🐠</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#d97706' }}>trio</div>
                <div style={{ fontSize: '12px', color: '#d97706' }}>{Math.round(selectedBounty.reward_tokens * 2)} tokens each</div>
              </div>
              
              <div style={{
                textAlign: 'center',
                padding: Branding.spacing.sm,
                backgroundColor: Branding.colors.pureWhite,
                borderRadius: Branding.borders.radius.md,
                border: '2px solid #ef4444'
              }}>
                <div style={{ fontSize: '16px', marginBottom: '4px' }}>🐠🐠🐠🐠</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#dc2626' }}>squad</div>
                <div style={{ fontSize: '12px', color: '#dc2626' }}>{Math.round(selectedBounty.reward_tokens * 2.5)} tokens each</div>
              </div>
            </div>
            
            <div style={{
              fontSize: '12px',
              color: '#64748b',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              working together gives everyone more tokens AND better learning
            </div>
          </div>

          <p style={{
            fontSize: Branding.typography.fontSize.md,
            color: Branding.colors.darkSlate,
            lineHeight: '1.6',
            marginBottom: Branding.spacing.lg
          }}>
            {selectedBounty.description}
          </p>

          {/* Enhanced Action buttons with collaboration focus */}
          <div style={{
            display: 'flex',
            gap: Branding.spacing.md,
            flexWrap: 'wrap'
          }}>
            <FeatureGate
              feature="GITHUB_BOUNTIES"
              userTier={userTier}
              user={user}
              fallback={
                <Link 
                  to="/pricing"
                  style={{
                    backgroundColor: Branding.colors.vibrantOrange,
                    color: Branding.colors.pureWhite,
                    padding: `${Branding.spacing.md} ${Branding.spacing.lg}`,
                    borderRadius: Branding.borders.radius.md,
                    textDecoration: 'none',
                    fontSize: Branding.typography.fontSize.md,
                    fontWeight: Branding.typography.fontWeight.medium
                  }}
                >
                  Upgrade to Start Bounty
                </Link>
              }
            >
              {/* Primary CTA: Invite Friends (highest tokens) */}
              <button
                onClick={() => {
                  // Start bounty and immediately trigger friend invitation
                  // This connects to the existing ContextualInviteModal system
                  alert(`🐠 Math Squad Assembly! You've started "${selectedBounty.title}" - now invite friends to earn ${Math.round(selectedBounty.reward_tokens * 2.5)} tokens each! Use the voice chat or share button to get your squad together.`);
                }}
                style={{
                  backgroundColor: '#10b981', // Ecosystem green - highest reward
                  color: Branding.colors.pureWhite,
                  padding: `${Branding.spacing.md} ${Branding.spacing.lg}`,
                  borderRadius: Branding.borders.radius.md,
                  border: 'none',
                  fontSize: Branding.typography.fontSize.md,
                  fontWeight: Branding.typography.fontWeight.bold,
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                🐠🐠🐠 Start with Friends ({Math.round(selectedBounty.reward_tokens * 2.5)} tokens each!)
              </button>
              
              {/* Secondary option: Start solo */}
              <button
                onClick={() => {
                  alert(`🐠 Solo swimmer! You've started "${selectedBounty.title}" for ${selectedBounty.reward_tokens} tokens. Remember, you can always invite friends later for bonus tokens!`);
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: Branding.colors.tealBlue,
                  padding: `${Branding.spacing.md} ${Branding.spacing.lg}`,
                  borderRadius: Branding.borders.radius.md,
                  border: `2px solid ${Branding.colors.tealBlue}`,
                  fontSize: Branding.typography.fontSize.md,
                  fontWeight: Branding.typography.fontWeight.medium,
                  cursor: 'pointer'
                }}
              >
                🐠 Start Solo ({selectedBounty.reward_tokens} tokens)
              </button>
            </FeatureGate>

            <Link 
              to="/tutor"
              style={{
                backgroundColor: 'transparent',
                color: Branding.colors.tealBlue,
                padding: `${Branding.spacing.md} ${Branding.spacing.lg}`,
                borderRadius: Branding.borders.radius.md,
                border: `2px solid ${Branding.colors.tealBlue}`,
                textDecoration: 'none',
                fontSize: Branding.typography.fontSize.md,
                fontWeight: Branding.typography.fontWeight.medium
              }}
            >
              💬 Chat with Mira
            </Link>
          </div>
        </div>

        {/* Bounty details sections */}
        <div style={{
          backgroundColor: Branding.colors.pureWhite,
          padding: Branding.spacing.xl,
          borderRadius: Branding.borders.radius.lg,
          border: `1px solid ${Branding.colors.veryLightGray}`
        }}>
          <h3 style={{
            fontSize: Branding.typography.fontSize.lg,
            fontWeight: Branding.typography.fontWeight.semibold,
            color: Branding.colors.darkSlate,
            marginBottom: Branding.spacing.md
          }}>
            What You'll Learn
          </h3>
          <ul style={{
            color: Branding.colors.darkSlate,
            lineHeight: '1.6',
            paddingLeft: Branding.spacing.lg
          }}>
            <li>Game development basics using Scratch or web technologies</li>
            <li>Educational game design principles</li>
            <li>Creative problem-solving and project management</li>
            <li>Math teaching strategies and common misconceptions</li>
            <li>User testing and iterative improvement</li>
          </ul>

          <h3 style={{
            fontSize: Branding.typography.fontSize.lg,
            fontWeight: Branding.typography.fontWeight.semibold,
            color: Branding.colors.darkSlate,
            marginTop: Branding.spacing.lg,
            marginBottom: Branding.spacing.md
          }}>
            Next Steps
          </h3>
          <ol style={{
            color: Branding.colors.darkSlate,
            lineHeight: '1.6',
            paddingLeft: Branding.spacing.lg
          }}>
            <li>Click "Start This Bounty" to accept the challenge</li>
            <li>Chat with Mira about your preferred development platform</li>
            <li>Begin with simple game mechanics and iterate</li>
            <li>Test with friends and get feedback</li>
            <li>Submit your completed game for review</li>
          </ol>
        </div>
      </div>
    );
  }

  // Bounty marketplace list view
  return (
    <div style={{
      padding: Branding.spacing.xl,
      maxWidth: '1000px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: Branding.spacing.xl
      }}>
        <h1 style={{
          fontSize: Branding.typography.fontSize.xxl,
          fontWeight: Branding.typography.fontWeight.bold,
          color: Branding.colors.darkSlate,
          marginBottom: Branding.spacing.sm
        }}>
          🏆 Bounty Marketplace
        </h1>
        <p style={{
          fontSize: Branding.typography.fontSize.md,
          color: Branding.colors.slateGray,
          lineHeight: '1.6'
        }}>
          Build your skills and portfolio by completing real-world challenges. 
          Earn tokens and get mentored by Mira throughout your journey.
        </p>
      </div>

      {/* Bounty grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: Branding.spacing.lg
      }}>
        {bounties.map((bounty, index) => (
          <div 
            key={index}
            onClick={() => handleStartBounty(bounty)}
            style={{
              backgroundColor: Branding.colors.pureWhite,
              padding: Branding.spacing.lg,
              borderRadius: Branding.borders.radius.lg,
              border: `1px solid ${Branding.colors.veryLightGray}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              ':hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            {/* Bounty header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: Branding.spacing.md
            }}>
              <h3 style={{
                fontSize: Branding.typography.fontSize.lg,
                fontWeight: Branding.typography.fontWeight.semibold,
                color: Branding.colors.darkSlate,
                marginBottom: Branding.spacing.xs,
                flex: 1
              }}>
                {getDifficultyEmoji(bounty.difficulty)} {bounty.title}
              </h3>
              
              <div style={{
                backgroundColor: getDifficultyColor(bounty.difficulty),
                color: Branding.colors.pureWhite,
                padding: `${Branding.spacing.xs} ${Branding.spacing.sm}`,
                borderRadius: Branding.borders.radius.full,
                fontSize: Branding.typography.fontSize.sm,
                fontWeight: Branding.typography.fontWeight.medium,
                marginLeft: Branding.spacing.sm
              }}>
                {bounty.reward_tokens} tokens
              </div>
            </div>

            {/* Topics */}
            <div style={{
              fontSize: Branding.typography.fontSize.xs,
              color: Branding.colors.slateGray,
              marginBottom: Branding.spacing.md
            }}>
              {bounty.difficulty} • {bounty.topics?.join(', ')}
            </div>

            {/* Description */}
            <p style={{
              fontSize: Branding.typography.fontSize.sm,
              color: Branding.colors.darkSlate,
              lineHeight: '1.5',
              marginBottom: Branding.spacing.sm
            }}>
              {bounty.description?.substring(0, 150)}...
            </p>

            {/* 🐠 Collaboration Multiplier Hint */}
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: Branding.borders.radius.md,
              padding: Branding.spacing.sm,
              marginBottom: Branding.spacing.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ fontSize: '14px' }}>🐠</span>
                <span style={{
                  fontSize: '11px',
                  color: '#0c4a6e',
                  fontWeight: '500'
                }}>
                  work together for bonus tokens
                </span>
              </div>
              <div style={{
                display: 'flex',
                gap: '4px',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '10px', color: '#6b7280' }}>up to</span>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#dc2626'
                }}>
                  {Math.round(bounty.reward_tokens * 2.5)} tokens
                </span>
              </div>
            </div>

            {/* Action button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleStartBounty(bounty);
              }}
              style={{
                backgroundColor: Branding.colors.tealBlue,
                color: Branding.colors.pureWhite,
                padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                borderRadius: Branding.borders.radius.md,
                border: 'none',
                fontSize: Branding.typography.fontSize.sm,
                fontWeight: Branding.typography.fontWeight.medium,
                cursor: 'pointer',
                width: '100%'
              }}
            >
              View Details →
            </button>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {bounties.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: Branding.spacing.xxl,
          color: Branding.colors.slateGray
        }}>
          <h3 style={{ marginBottom: Branding.spacing.md }}>No bounties available</h3>
          <p>Check back soon for new challenges!</p>
        </div>
      )}
    </div>
  );
};

export default BountyMarketplace;